import BaseProduct from './BaseProduct';
import { ProductId, DroughtMode } from '../types';
import store, { getLayerOpacity } from '../state';
import maplibregl from 'maplibre-gl';

export class DroughtLayer extends BaseProduct {
  public readonly id: ProductId = 'drought';
  private pinnedPopup: maplibregl.Popup | null = null;
  private hoverPopup: maplibregl.Popup | null = null;
  private mapEventsBound: boolean = false;
  private unsubscribeStore: (() => void) | null = null;
  private currentMode: DroughtMode = 'usdm';

  protected onActivate(): void {
    this.refresh();
    this.bindMapEvents();

    this.unsubscribeStore = store.subscribe((state) => {
      if (!this.active) return;
      const newMode = (state.droughtMode as DroughtMode) || 'usdm';
      if (newMode !== this.currentMode) {
        this.currentMode = newMode;
        this.refresh();
      }
    });
  }

  protected onDeactivate(): void {
    if (this.unsubscribeStore) {
      this.unsubscribeStore();
      this.unsubscribeStore = null;
    }
    this.removeLayers();
    this.unbindMapEvents();
  }

  protected onTimeUpdate(_date: string, _hour: string): void {
    this.refresh();
  }

  public refresh(): void {
    if (!this.active) return;
    this.setupLayers();
    this.updateVisibility();
  }

  private getDataUrl(): string {
    const state = store.getState();
    const manifest = state.manifest;
    let apiServer = manifest?.api_server;
    if (apiServer === undefined || apiServer === null) {
      apiServer = 'https://auth.skyskout.com/';
    } else if (apiServer === '') {
      const path = window.location.pathname;
      const dir = path.substring(0, path.lastIndexOf('/') + 1);
      apiServer = window.location.origin + dir;
    }
    if (!apiServer.endsWith('/')) apiServer += '/';
    const tileRoot = (manifest?.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
    const versionParam = manifest?.generated_at ? `?v=${encodeURIComponent(manifest.generated_at)}` : `?t=${Date.now()}`;

    const filename = this.currentMode === 'outlook' ? 'drought_outlook.geojson' : 'usdm_current.geojson';
    return `${apiServer}${tileRoot}/observed/drought/${filename}${versionParam}`;
  }

  private setupLayers(): void {
    const dataUrl = this.getDataUrl();
    const sourceId = 'drought-source';

    if (!this.map.getSource(sourceId)) {
      this.map.addSource(sourceId, {
        type: 'geojson',
        data: dataUrl
      });
    } else {
      const source = this.map.getSource(sourceId) as maplibregl.GeoJSONSource;
      if (source && typeof source.setData === 'function') {
        source.setData(dataUrl);
      }
    }

    // Dynamic layer ordering: insert beneath city labels but above background basemap fills
    const layers = this.map.getStyle()?.layers;
    let beforeId: string | undefined = undefined;
    if (layers) {
      const firstSymbol = layers.find(l => l.type === 'symbol');
      if (firstSymbol) beforeId = firstSymbol.id;
    }

    const opacity = getLayerOpacity(this.id, 1.0);

    // 1. Fill Layer
    if (!this.map.getLayer('drought-fill')) {
      const fillColorExpr = this.currentMode === 'outlook'
        ? [
            'match',
            ['get', 'code'],
            'persist', '#991b1b',
            'develop', '#f97316',
            'improve', '#fbbf24',
            'remove', '#22c55e',
            '#38bdf8'
          ]
        : [
            'match',
            ['get', 'dm'],
            0, '#fde047',
            1, '#fbbf24',
            2, '#f97316',
            3, '#ef4444',
            4, '#991b1b',
            '#e2e8f0'
          ];

      this.map.addLayer({
        id: 'drought-fill',
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': fillColorExpr as any,
          'fill-opacity': 0.42 * opacity
        }
      }, beforeId);
    } else {
      const fillColorExpr = this.currentMode === 'outlook'
        ? [
            'match',
            ['get', 'code'],
            'persist', '#991b1b',
            'develop', '#f97316',
            'improve', '#fbbf24',
            'remove', '#22c55e',
            '#38bdf8'
          ]
        : [
            'match',
            ['get', 'dm'],
            0, '#fde047',
            1, '#fbbf24',
            2, '#f97316',
            3, '#ef4444',
            4, '#991b1b',
            '#e2e8f0'
          ];
      this.map.setPaintProperty('drought-fill', 'fill-color', fillColorExpr);
      this.map.setPaintProperty('drought-fill', 'fill-opacity', 0.42 * opacity);
    }

    // 2. Stroke Layer
    if (!this.map.getLayer('drought-stroke')) {
      const strokeColorExpr = this.currentMode === 'outlook'
        ? [
            'match',
            ['get', 'code'],
            'persist', '#7f1d1d',
            'develop', '#c2410c',
            'improve', '#d97706',
            'remove', '#15803d',
            '#0284c7'
          ]
        : [
            'match',
            ['get', 'dm'],
            0, '#ca8a04',
            1, '#d97706',
            2, '#c2410c',
            3, '#b91c1c',
            4, '#450a0a',
            '#94a3b8'
          ];

      this.map.addLayer({
        id: 'drought-stroke',
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': strokeColorExpr as any,
          'line-width': 1.2,
          'line-opacity': 0.75 * opacity
        }
      }, beforeId);
    } else {
      const strokeColorExpr = this.currentMode === 'outlook'
        ? [
            'match',
            ['get', 'code'],
            'persist', '#7f1d1d',
            'develop', '#c2410c',
            'improve', '#d97706',
            'remove', '#15803d',
            '#0284c7'
          ]
        : [
            'match',
            ['get', 'dm'],
            0, '#ca8a04',
            1, '#d97706',
            2, '#c2410c',
            3, '#b91c1c',
            4, '#450a0a',
            '#94a3b8'
          ];
      this.map.setPaintProperty('drought-stroke', 'line-color', strokeColorExpr);
      this.map.setPaintProperty('drought-stroke', 'line-opacity', 0.75 * opacity);
    }
  }

  private updateVisibility(): void {
    const visibility = this.active ? 'visible' : 'none';
    if (this.map.getLayer('drought-fill')) {
      this.map.setLayoutProperty('drought-fill', 'visibility', visibility);
    }
    if (this.map.getLayer('drought-stroke')) {
      this.map.setLayoutProperty('drought-stroke', 'visibility', visibility);
    }
  }

  private removeLayers(): void {
    if (this.pinnedPopup) {
      this.pinnedPopup.remove();
      this.pinnedPopup = null;
    }
    if (this.hoverPopup) {
      this.hoverPopup.remove();
      this.hoverPopup = null;
    }
    if (this.map.getLayer('drought-stroke')) {
      this.map.removeLayer('drought-stroke');
    }
    if (this.map.getLayer('drought-fill')) {
      this.map.removeLayer('drought-fill');
    }
    if (this.map.getSource('drought-source')) {
      this.map.removeSource('drought-source');
    }
  }

  private bindMapEvents(): void {
    if (this.mapEventsBound) return;
    this.mapEventsBound = true;

    this.map.on('mouseenter', 'drought-fill', () => {
      this.map.getCanvas().style.cursor = 'pointer';
    });

    this.map.on('mouseleave', 'drought-fill', () => {
      this.map.getCanvas().style.cursor = '';
      if (this.hoverPopup && !this.pinnedPopup) {
        this.hoverPopup.remove();
        this.hoverPopup = null;
      }
    });

    this.map.on('mousemove', 'drought-fill', (e) => {
      if (this.pinnedPopup) return;
      if (!e.features || e.features.length === 0) return;

      const feature = e.features[0];
      const props = feature.properties || {};
      const html = this.buildPopupHtml(props);

      if (!this.hoverPopup) {
        this.hoverPopup = new maplibregl.Popup({
          closeButton: false,
          closeOnClick: false,
          className: 'skyskout-drought-popup',
          offset: 12
        });
      }

      this.hoverPopup.setLngLat(e.lngLat).setHTML(html).addTo(this.map);
    });

    this.map.on('click', 'drought-fill', (e) => {
      if (!e.features || e.features.length === 0) return;
      const feature = e.features[0];
      const props = feature.properties || {};
      const html = this.buildPopupHtml(props);

      if (this.hoverPopup) {
        this.hoverPopup.remove();
        this.hoverPopup = null;
      }

      if (this.pinnedPopup) {
        this.pinnedPopup.remove();
      }

      this.pinnedPopup = new maplibregl.Popup({
        closeButton: true,
        closeOnClick: true,
        className: 'skyskout-drought-popup',
        offset: 12
      })
        .setLngLat(e.lngLat)
        .setHTML(html)
        .addTo(this.map);

      this.pinnedPopup.on('close', () => {
        this.pinnedPopup = null;
      });
    });
  }

  private unbindMapEvents(): void {
    this.mapEventsBound = false;
  }

  private buildPopupHtml(props: any): string {
    const isOutlook = this.currentMode === 'outlook';
    const title = isOutlook ? props.label || props.outlook || 'Drought Outlook' : `${props.code || 'D?'} — ${props.label || 'Drought Area'}`;
    const badgeColor = props.color || '#fbbf24';
    const desc = props.description || '';
    const dateLabel = isOutlook ? (props.target_period ? `Target: ${props.target_period}` : '') : (props.valid_as_of ? `Issued: ${props.valid_as_of}` : '');

    return `
      <div style="font-family: 'Plus Jakarta Sans', system-ui, sans-serif; padding: 10px 12px; max-width: 280px; background: rgba(15, 23, 42, 0.95); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 10px; color: #f8fafc; backdrop-filter: blur(8px); box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.5);">
        <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 6px;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="display: inline-block; width: 10px; height: 10px; border-radius: 50%; background: ${badgeColor}; box-shadow: 0 0 8px ${badgeColor};"></span>
            <span style="font-weight: 700; font-size: 0.88rem; color: #f8fafc;">${title}</span>
          </div>
          ${dateLabel ? `<span style="font-size: 0.70rem; color: #94a3b8; font-weight: 500;">${dateLabel}</span>` : ''}
        </div>
        ${desc ? `<div style="font-size: 0.78rem; line-height: 1.35; color: #cbd5e1; margin-top: 4px;">${desc}</div>` : ''}
        <div style="font-size: 0.68rem; color: #64748b; margin-top: 6px; text-align: right;">
          ${isOutlook ? 'NOAA Climate Prediction Center' : 'NDMC / USDA / NOAA'}
        </div>
      </div>
    `;
  }
}

export default DroughtLayer;
